import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { subjectApi, type Subject as ApiSubject } from "@/api/subject";

export type Subject = ApiSubject;

export interface SubjectFilters {
    search?: string;
    grade_level?: string;
    major_id?: string;
    is_active?: boolean;
    page?: number;
    per_page?: number;
}

export const useSubjectStore = defineStore("subject", () => {
    // State
    const subjects = ref<Subject[]>([]);
    const currentSubject = ref<ApiSubject | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);
    const page = ref(1);
    const perPage = ref(10);
    const search = ref("");
    const gradeLevel = ref("");
    const major = ref("");
    const isActive = ref<boolean | null>(null);
    const meta = ref<{
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from?: number;
        to?: number;
    } | null>(null);

    // Getters
    const activeSubjects = computed(() =>
        subjects.value.filter((subject) => subject.is_active),
    );

    const subjectsByGradeLevel = computed(() => {
        const grouped: Record<string, Subject[]> = {};
        subjects.value.forEach((subject) => {
            const grade = subject.grade_level || "Unassigned";
            if (!grouped[grade]) {
                grouped[grade] = [];
            }
            grouped[grade].push(subject);
        });
        return grouped;
    });

    const subjectsByMajor = computed(() => {
        const grouped: Record<string, Subject[]> = {};
        subjects.value.forEach((subject) => {
            const major = subject.major?.name || "No Major";
            if (!grouped[major]) {
                grouped[major] = [];
            }
            grouped[major].push(subject);
        });
        return grouped;
    });

    // Actions
    const fetchSubjects = async (filters: SubjectFilters = {}) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await subjectApi.getAll({
                page: page.value,
                per_page: perPage.value,
                search: search.value || undefined,
                grade_level: gradeLevel.value || undefined,
                major_id: major.value || undefined,
                is_active: isActive.value !== null ? isActive.value : undefined,
                ...filters,
            });
            subjects.value = response.data?.items || response.data || [];
            const metaData = response.data?.meta || null;
            if (metaData) {
                meta.value = metaData;
                page.value = metaData.current_page;
                perPage.value = metaData.per_page;
            }
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch subjects";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchSubjectById = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await subjectApi.getById(id);
            currentSubject.value = response;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch subject";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const createSubject = async (data: any) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await subjectApi.create(data);
            await fetchSubjects(); // Refresh the list
            return response;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to create subject";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const updateSubject = async (id: string, data: any) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await subjectApi.update(id, data);
            await fetchSubjects(); // Refresh the list
            return response;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to update subject";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const deleteSubject = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            await subjectApi.delete(id);
            await fetchSubjects(); // Refresh the list
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to delete subject";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const clearError = () => {
        error.value = null;
    };

    const setPage = (newPage: number) => {
        page.value = newPage;
        fetchSubjects();
    };

    const setPerPage = (newPerPage: number) => {
        perPage.value = newPerPage;
        page.value = 1; // Reset to first page
        fetchSubjects();
    };

    const resetFilters = () => {
        search.value = "";
        gradeLevel.value = "";
        major.value = "";
        isActive.value = null;
        page.value = 1;
        fetchSubjects();
    };

    return {
        // State
        subjects,
        currentSubject,
        loading,
        error,
        page,
        perPage,
        search,
        gradeLevel,
        major,
        isActive,
        meta,

        // Getters
        activeSubjects,
        subjectsByGradeLevel,
        subjectsByMajor,

        // Actions
        fetchSubjects,
        fetchSubjectById,
        createSubject,
        updateSubject,
        deleteSubject,
        clearError,
        setPage,
        setPerPage,
        resetFilters,
    };
});
